import { assertFileWithinUploadLimit } from '@/lib/fileUploadLimits'

import fetchClient, { ApiResponse } from '../api-client'
import { CreatePickupOrderPayload, PickupOrderCancelReason, PickupOrderStatus, PickupOrderWithUser } from './types/PickupOrder'
import { PickupOrder } from './types/PickupOrder'

/**
 * Create a new pickup order
 */
export const createPickupOrder = async (data: CreatePickupOrderPayload): Promise<PickupOrder> => {
  try {
    const formData = new FormData()
    formData.append('pickupDate', data.pickupDate)
    formData.append('orderNumber', data.orderNumber)
    if (data.barcodeFile) {
      assertFileWithinUploadLimit(data.barcodeFile)
      formData.append('barcodeFile', data.barcodeFile)
    }

    // Don't set Content-Type header - axios will set it automatically with boundary for FormData
    const res = await fetchClient.post<ApiResponse<PickupOrder>>(`/pickup-orders`, formData)
    if (!res.data.data) {
      throw new Error('Failed to create pickup order')
    }
    return res.data.data
  } catch (err) {
    console.error('createPickupOrder error:', err)
    throw err
  }
}

/**
 * Get all pickup orders with optional filtering
 */
export const getPickupOrders = async (): Promise<PickupOrderWithUser[]> => {
  try {
    const res = await fetchClient.get<ApiResponse<PickupOrderWithUser[]>>(`/pickup-orders`)
    return res.data.data || []
  } catch (error) {
    throw error
  }
}

/**
 * Get a single pickup order by ID
 */
export const getPickupOrder = async (id: string): Promise<PickupOrder> => {
  try {
    const res = await fetchClient.get<ApiResponse<PickupOrder>>(`/pickup-orders/${id}`)
    return res.data.data
  } catch (error) {
    throw error
  }
}

/**
 * Update a pickup order
 */
export const updatePickupOrder = async (
  id: string,
  data: { status: PickupOrderStatus; pkgCount?: number; cancelReason?: PickupOrderCancelReason }
): Promise<PickupOrder> => {
  try {
    const res = await fetchClient.patch<ApiResponse<PickupOrder>>(`/pickup-orders/${id}`, data)
    if (!res.data.data) {
      throw new Error('Failed to update pickup order')
    }
    return res.data.data
  } catch (err) {
    console.error('updatePickupOrder error:', err)
    throw err
  }
}

/**
 * Delete a pickup order (backoffice only)
 */
export const deletePickupOrder = async (id: string): Promise<void> => {
  await fetchClient.delete(`/pickup-orders/${id}`)
}

/**
 * Upload a new barcode for a pickup order (when status is false_barcode)
 */
export const uploadPickupOrderBarcode = async (id: string, barcodeFile: File): Promise<PickupOrder> => {
  try {
    assertFileWithinUploadLimit(barcodeFile)
    const formData = new FormData()
    formData.append('barcodeFile', barcodeFile)
    const res = await fetchClient.patch<ApiResponse<PickupOrder>>(`/pickup-orders/${id}/barcode`, formData)
    if (!res.data.data) {
      throw new Error('Failed to upload barcode')
    }
    return res.data.data
  } catch (err) {
    console.error('uploadPickupOrderBarcode error:', err)
    throw err
  }
}
